import React from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Shield, Plus, CalendarClock, Users, FileText, QrCode } from 'lucide-react';

/**
 * AuthorityMiniDashboard — Iter 205a.
 *
 * Compact authority-aware header card. Shown at the top of:
 *   • /safety-bulletin (the public feed)
 *   • /profile (the user's profile page)
 *
 * Surfaces the five things the user explicitly asked to be obvious right
 * after an official authority logs in:
 *
 *   1. Who they are posting as (display name + OFFICIAL badge)
 *   2. Which town they are locked to
 *   3. How many residents are following their town
 *   4. How to publish a new Safety Alert  (primary CTA)
 *   5. How to manage scheduled alerts     (secondary CTA)
 *
 * Renders nothing when `isOfficial` is false so resident accounts are
 * untouched.
 */
const AuthorityMiniDashboard = ({
  isOfficial, source, town, displayName, followers, publishedLast30d,
  variant = 'feed',  // 'feed' | 'profile'
}) => {
  if (!isOfficial) return null;
  const Icon = source === 'police' ? Shield : Landmark;
  const sourceLabel = source === 'police' ? 'POLICE' : 'TOWN HALL';

  return (
    <section
      className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-amber-50 to-orange-50 p-4 mb-5 shadow-sm"
      data-testid={`authority-mini-dashboard-${variant}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-amber-600 text-white flex items-center justify-center shadow-md shrink-0">
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
            Official Safety Source · {sourceLabel}
          </p>
          <h2
            className="text-lg font-extrabold text-slate-900 truncate"
            data-testid="authority-display-name"
          >
            {displayName || 'Authority account'}
          </h2>
          <p className="text-sm text-slate-700">
            <strong data-testid="authority-town-label">{town || '—'}</strong>
            <span className="mx-1.5 text-slate-300">·</span>
            <span data-testid="authority-followers-count">
              {followers > 0
                ? `${followers.toLocaleString('en-GB')} resident${followers === 1 ? '' : 's'} following`
                : 'Be the first to gain followers'}
            </span>
            {publishedLast30d > 0 && (
              <>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="text-emerald-700 font-medium">
                  {publishedLast30d} alert{publishedLast30d === 1 ? '' : 's'} in 30d
                </span>
              </>
            )}
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Link
          to="/safety-bulletin/submit"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold shadow-sm transition-colors"
          data-testid="authority-cta-publish"
        >
          <Plus className="h-4 w-4" /> Publish Safety Alert
        </Link>
        <Link
          to="/authority/scheduled"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-sm font-semibold transition-colors"
          data-testid="authority-cta-scheduled"
        >
          <CalendarClock className="h-4 w-4" /> My Scheduled Alerts
        </Link>
      </div>
      {variant === 'profile' && (
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-amber-800 border-t border-amber-200 pt-3">
          <Link
            to="/admin/authority-onboarding"
            className="inline-flex items-center gap-1 hover:underline"
            data-testid="authority-link-onboarding-guide"
          >
            {/* Aug 2026 bug-fix — removed `target="_blank" rel="noopener"`.
                The guide route is auth-gated. When the operator signs in
                without "Remember me", the JWT lives in sessionStorage which
                is NOT shared across tabs — opening in a new tab boots the
                new tab with `user=null`, and the guard bounced to
                /admin/login. Same-tab navigation preserves the session and
                the browser Print dialog + Back button give the same UX as
                a new tab. */}
            <FileText className="h-3.5 w-3.5" /> Printable onboarding guide
          </Link>
          <Link
            to={`/safety-bulletin?town=${encodeURIComponent(town)}`}
            className="inline-flex items-center gap-1 hover:underline"
            data-testid="authority-link-public-feed"
          >
            <Users className="h-3.5 w-3.5" /> View public feed
          </Link>
          <span className="inline-flex items-center gap-1 opacity-60" title="Admins can generate town QR posters from the Safety admin tab">
            <QrCode className="h-3.5 w-3.5" /> QR poster: ask admin
          </span>
        </div>
      )}
    </section>
  );
};

export default AuthorityMiniDashboard;
